'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher';
import { TRACKED_EVENT_POSITION } from '@/constants';
import type { TocItem } from '../_utils/toc';
import { useActiveHeading } from './useActiveHeading.hook';

interface Props {
  items: TocItem[];
}

/**
 * 목차 — 본문 h2 앵커 목록. 현재 읽는 위치를 파란색으로 표시한다.
 *
 * 데스크톱 우 레일 전용이다. 호출부가 `hidden … xl:flex` 로 감싸 노출을 가른다.
 *
 * 항목 0건이면 아무것도 렌더하지 않는다 — 제목만 남은 빈 박스를 만들지 않기 위해서다.
 */
export default function TableOfContents({ items }: Props) {
  const t = useTranslations('Blog.main');
  const activeId = useActiveHeading(items.map(item => item.id));

  if (items.length === 0) return null;

  return (
    <nav aria-label={t('toc')}>
      <ul className="flex flex-col gap-2.5">
        {items.map((item, index) => {
          const isActive = item.id === activeId;

          return (
            <li key={item.id}>
              <TrackedAnchor
                label={`toc_item_${index + 1}`}
                position={TRACKED_EVENT_POSITION.RIGHT_SIDEBAR}
                href={`#${item.id}`}
                // 목차 이동은 히스토리를 쌓지 않는다 — push 로 두면 목차를 n 번 누른
                // 뒤 뒤로가기가 같은 글 안에서 n 번 되돌아가야 이전 페이지로 나간다
                replace
                aria-current={isActive ? 'location' : undefined}
                className="flex items-center gap-3"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-[46px] w-[2px] shrink-0 rounded-full',
                    isActive ? 'bg-3b-blue-light-600' : 'bg-3b-text-hint',
                  )}
                />
                <span
                  className={cn(
                    'line-clamp-2 text-[14px] font-medium leading-6 tracking-[-0.176px]',
                    isActive
                      ? 'text-3b-blue-light-600'
                      : 'text-3b-text-primary',
                  )}
                >
                  {item.label}
                </span>
              </TrackedAnchor>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
